import React, { useState } from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRowPeers } from '../../context/PresenceContext';
import { profilePictureUrl, memberInitials } from '../../utils/profilePicture';
import ProfilePicturePreview from '../ProfilePicturePreview';
import { PRESENCE_ACTIVE_CLASS } from '../../hooks/useGridPresence';

const MAX_FACES = 2; // the column is narrow: two faces, then "+N"
const SIZE = 20;
const OVERLAP_PX = 6;

const displayName = (member) => (
  `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.username || member.email
);

/**
 * Avatars of the other people on this row (the marker column's cell).
 * Empty when nobody else is on it. A face with a picture enlarges on click.
 */
const RowPresenceCell = ({ grid, rowId }) => {
  const theme = useTheme();
  const rowPeers = useRowPeers(grid, rowId);
  const [picturePreview, setPicturePreview] = useState(null);

  if (rowPeers.length === 0) return null;

  const overflow = rowPeers.length > MAX_FACES;
  const visible = overflow ? rowPeers.slice(0, MAX_FACES - 1) : rowPeers;
  const hidden = overflow ? rowPeers.slice(MAX_FACES - 1) : [];
  const avatarSx = {
    width: SIZE,
    height: SIZE,
    fontSize: 9,
    border: `1.5px solid ${theme.palette.background.paper}`,
  };
  const label = ({ member, editing }) => `${displayName(member)} ${editing ? 'is editing' : 'is on'} this row`;

  return (
    // The class lets the grid tint the whole row (PRESENCE_ROW_SX, via :has()).
    <Box className={PRESENCE_ACTIVE_CLASS} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {visible.map((peer, index) => {
        const url = profilePictureUrl(peer.member.email, peer.member.profile_picture_version);
        return (
          <Tooltip key={peer.member.email} title={label(peer)}>
            <Avatar
              src={url || undefined}
              alt={displayName(peer.member)}
              onClick={url ? (event) => {
                event.stopPropagation(); // don't also click the row
                setPicturePreview({ url, alt: displayName(peer.member) });
              } : undefined}
              aria-label={label(peer)}
              sx={{
                ...avatarSx,
                cursor: url ? 'pointer' : 'default',
                ml: index === 0 ? 0 : `-${OVERLAP_PX}px`,
                zIndex: visible.length - index + 1,
              }}
            >
              {memberInitials(peer.member.first_name, peer.member.last_name)}
            </Avatar>
          </Tooltip>
        );
      })}
      {overflow && (
        <Tooltip title={hidden.map(label).join(' · ')}>
          <Avatar sx={{ ...avatarSx, fontSize: 8, fontWeight: 600, ml: `-${OVERLAP_PX}px`, zIndex: 0 }}>
            +{hidden.length}
          </Avatar>
        </Tooltip>
      )}
      <ProfilePicturePreview picture={picturePreview} onClose={() => setPicturePreview(null)} />
    </Box>
  );
};

export default RowPresenceCell;
